import { useQuery } from '@tanstack/react-query';
import { listEventos } from '@/api/clientes';
import { ESTADO_META } from './EstadoSelect';
import styles from './EventosHistorial.module.css';

interface Props {
  hitoId: string;
}

/**
 * Expander inline con el historial completo de cambios de estado del hito.
 * Carga lazy con TanStack Query.
 */
export function EventosHistorial({ hitoId }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hito-eventos', hitoId],
    queryFn: () => listEventos(hitoId),
    staleTime: 30 * 1000,
  });

  if (isLoading) {
    return <div className={styles.wrap}>Cargando historial…</div>;
  }
  if (error || !data) {
    return <div className={styles.wrap}>No se pudo cargar el historial.</div>;
  }
  if (data.length === 0) {
    return <div className={styles.wrap}>Sin eventos registrados.</div>;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.title}>Historial de cambios ({data.length})</div>
      <ul className={styles.list}>
        {data.map((e) => {
          const metaNuevo = ESTADO_META[e.estadoNuevo];
          const metaAnterior = e.estadoAnterior ? ESTADO_META[e.estadoAnterior] : null;
          const isBloqueoAbierto = e.estadoNuevo === 'bloqueado' && !e.resueltoEn;
          const isBloqueoCerrado = e.estadoNuevo === 'bloqueado' && !!e.resueltoEn;

          return (
            <li
              key={e.id}
              className={styles.item}
              style={{ borderLeftColor: metaNuevo.color }}
            >
              <div className={styles.itemHead}>
                <div className={styles.transition}>
                  {metaAnterior ? (
                    <>
                      <span
                        className={styles.pillEstado}
                        style={{ background: metaAnterior.bg, color: metaAnterior.color }}
                      >
                        {metaAnterior.label}
                      </span>
                      <span className={styles.arrow}>→</span>
                    </>
                  ) : (
                    <span className={styles.initialTag}>Inicial</span>
                  )}
                  <span
                    className={styles.pillEstado}
                    style={{ background: metaNuevo.bg, color: metaNuevo.color }}
                  >
                    {metaNuevo.label}
                  </span>
                  {isBloqueoAbierto && (
                    <span className={styles.bloqueoOpen}>● activo</span>
                  )}
                  {isBloqueoCerrado && (
                    <span className={styles.bloqueoClosed}>
                      resuelto {formatDate(e.resueltoEn!)}
                    </span>
                  )}
                </div>
                <span className={styles.date}>{formatDate(e.createdAt)}</span>
              </div>
              <div className={styles.comentario}>{e.comentario}</div>
              {e.evidenciaUrl && (
                <a
                  href={e.evidenciaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.evLink}
                >
                  🔗 Ver respaldo
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
